import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Trash2, Eye, EyeOff, Save, ChevronUp, ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSlider = () => {
  const navigate = useNavigate();
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSliderSettings();
  }, []);

  const fetchSliderSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/slider`);
      const data = await response.json();
      setSlides(data.slides || []);
    } catch (error) {
      console.error('Error fetching slider settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_URL}/api/admin/slider`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ slides })
      });

      if (!response.ok) {
        throw new Error('Failed to save');
      }

      alert('Slider ayarları kaydedildi!');
    } catch (error) {
      console.error('Error saving slider:', error);
      alert('Kaydetme hatası!');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (slideId, imageType, file) => {
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Sadece JPG, PNG, WEBP veya GIF formatında görsel yükleyebilirsiniz.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Görsel boyutu maksimum 5MB olmalıdır.');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API_URL}/api/admin/upload-slider-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        throw new Error('Upload failed');
      }

      const data = await response.json();
      
      // Update slide with new image URL
      updateSlide(slideId, imageType, data.url);
      
      alert('Görsel başarıyla yüklendi!');
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Görsel yükleme hatası!');
    }
  };

  const addSlide = () => {
    const newSlide = {
      id: Date.now(),
      title: 'Yeni Slider',
      subtitle: 'Açıklama yazın',
      productName: 'Kategori/Ürün Adı',
      image: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=1200',
      mobileImage: 'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=800',
      link: '/products',
      enabled: true
    };
    setSlides([...slides, newSlide]);
  };

  const updateSlide = (id, field, value) => {
    setSlides(slides.map(slide => 
      slide.id === id ? { ...slide, [field]: value } : slide
    ));
  };

  const deleteSlide = (id) => {
    if (window.confirm('Bu slider\'ı silmek istediğinizden emin misiniz?')) {
      setSlides(slides.filter(slide => slide.id !== id));
    }
  };

  const toggleEnabled = (id) => {
    setSlides(slides.map(slide =>
      slide.id === id ? { ...slide, enabled: !slide.enabled } : slide
    ));
  };

  const moveSlide = (index, direction) => {
    const newSlides = [...slides];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex >= 0 && newIndex < slides.length) {
      [newSlides[index], newSlides[newIndex]] = [newSlides[newIndex], newSlides[index]];
      setSlides(newSlides);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">Ana Sayfa Slider Yönetimi</h1>
            <p className="text-gray-600">Ana sayfadaki slider resimlerini ve yazılarını düzenleyin</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={addSlide}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus size={20} />
              Yeni Slider Ekle
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50"
            >
              <Save size={20} />
              {saving ? 'Kaydediliyor...' : 'Kaydet'}
            </button>
          </div>
        </div>

        {slides.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-600 mb-4">Henüz slider eklenmemiş</p>
            <button
              onClick={addSlide}
              className="bg-red-600 text-white px-6 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              İlk Slider'ı Ekle
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {slides.map((slide, index) => (
              <div key={slide.id} className={`bg-white rounded-lg shadow-md p-6 ${!slide.enabled ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => moveSlide(index, 'up')}
                        disabled={index === 0}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                        title="Yukarı taşı"
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        onClick={() => moveSlide(index, 'down')}
                        disabled={index === slides.length - 1}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-30"
                        title="Aşağı taşı"
                      >
                        <ChevronDown size={16} />
                      </button>
                    </div>
                    <span className="text-lg font-semibold text-gray-800">Slider #{index + 1}</span>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      slide.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {slide.enabled ? 'Aktif' : 'Pasif'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => toggleEnabled(slide.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        slide.enabled ? 'bg-green-100 text-green-600 hover:bg-green-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                      title={slide.enabled ? 'Pasif yap' : 'Aktif yap'}
                    >
                      {slide.enabled ? <Eye size={20} /> : <EyeOff size={20} />}
                    </button>
                    <button
                      onClick={() => deleteSlide(slide.id)}
                      className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
                      title="Sil"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Left Column - Text Fields */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ürün/Kategori Adı (Küçük Yazı)
                      </label>
                      <input
                        type="text"
                        value={slide.productName}
                        onChange={(e) => updateSlide(slide.id, 'productName', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        placeholder="Örn: Bluetooth Kulaklık"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Başlık (Büyük Yazı)
                      </label>
                      <input
                        type="text"
                        value={slide.title}
                        onChange={(e) => updateSlide(slide.id, 'title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        placeholder="Örn: Premium Ses Deneyimi"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Alt Başlık (Açıklama)
                      </label>
                      <textarea
                        value={slide.subtitle}
                        onChange={(e) => updateSlide(slide.id, 'subtitle', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        rows={3}
                        placeholder="Açıklama yazın"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Link (Tıklandığında Gidilecek Sayfa)
                      </label>
                      <input
                        type="text"
                        value={slide.link}
                        onChange={(e) => updateSlide(slide.id, 'link', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent"
                        placeholder="/category/kulakliklar"
                      />
                    </div>
                  </div>

                  {/* Right Column - Images */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Masaüstü Görsel URL
                      </label>
                      <input
                        type="text"
                        value={slide.image}
                        onChange={(e) => updateSlide(slide.id, 'image', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent mb-2"
                        placeholder="https://..."
                      />
                      
                      {/* File Upload Button */}
                      <div className="mb-2">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleImageUpload(slide.id, 'image', file);
                            }
                          }}
                          className="hidden"
                          id={`desktop-upload-${slide.id}`}
                        />
                        <label
                          htmlFor={`desktop-upload-${slide.id}`}
                          className="inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                        >
                          📤 Görsel Yükle
                        </label>
                        <span className="ml-2 text-xs text-gray-500">veya yukarıya URL yapıştırın</span>
                      </div>

                      {slide.image && (
                        <img
                          src={slide.image}
                          alt="Preview"
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/400x200?text=Görsel+Yüklenemedi'}
                        />
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Mobil Görsel URL
                      </label>
                      <input
                        type="text"
                        value={slide.mobileImage}
                        onChange={(e) => updateSlide(slide.id, 'mobileImage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-600 focus:border-transparent mb-2"
                        placeholder="https://..."
                      />
                      
                      {/* File Upload Button for Mobile */}
                      <div className="mb-2">
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleImageUpload(slide.id, 'mobileImage', file);
                            }
                          }}
                          className="hidden"
                          id={`mobile-upload-${slide.id}`}
                        />
                        <label
                          htmlFor={`mobile-upload-${slide.id}`}
                          className="inline-block px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                        >
                          📤 Görsel Yükle
                        </label>
                        <span className="ml-2 text-xs text-gray-500">veya yukarıya URL yapıştırın</span>
                      </div>

                      {slide.mobileImage && (
                        <img
                          src={slide.mobileImage}
                          alt="Mobile Preview"
                          className="w-full h-32 object-cover rounded-lg"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/400x200?text=Görsel+Yüklenemedi'}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-lg">
          <h3 className="font-semibold text-blue-900 mb-2">💡 İpuçları</h3>
          <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
            <li>Slider sıralamasını değiştirmek için yukarı/aşağı okları kullanın</li>
            <li>Göz ikonuna tıklayarak slider'ı aktif/pasif yapabilirsiniz</li>
            <li>Görseller için Unsplash veya kendi sunucunuzdaki URL'leri kullanabilirsiniz</li>
            <li>Link alanına kategori (/category/kulakliklar) veya ürün (/product/123) linki yazabilirsiniz</li>
            <li>Değişikliklerinizi kaydetmeyi unutmayın!</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSlider;
